/**
 * CloudWatch RUM Provider for SleepHistory
 *
 * Initializes AWS CloudWatch Real User Monitoring for frontend observability.
 * Tracks page views, errors, performance, and custom events.
 */

import React, { useEffect } from "react";
import type { AwsRum } from "aws-rum-web";
import {
  captureUserContext,
  updateSessionContext,
  fetchIpLocation,
  captureGeolocation,
  type UserContextData,
} from "../utils/rum-user-context";

let awsRum: AwsRum | null = null;
let globalUserContext: UserContextData | null = null;

/**
 * Initialize AWS CloudWatch RUM for frontend monitoring
 * Called once when the app loads
 */
export function initializeRum() {
  // Only run on client-side
  if (typeof window === "undefined") return null;

  // Return existing instance if already initialized
  if (awsRum) return awsRum;

  // Check for required environment variables (REACT_APP_ prefix for CRA)
  const identityPoolId = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
  const guestRoleArn = process.env.REACT_APP_RUM_GUEST_ROLE_ARN;
  const appMonitorId = process.env.REACT_APP_RUM_APP_MONITOR_ID;
  const region = process.env.REACT_APP_RUM_REGION || "us-east-1";

  if (!identityPoolId || !guestRoleArn || !appMonitorId) {
    console.warn(
      "⚠️  CloudWatch RUM not configured: Missing required environment variables"
    );
    console.warn(
      "  Required: REACT_APP_RUM_IDENTITY_POOL_ID, REACT_APP_RUM_GUEST_ROLE_ARN, REACT_APP_RUM_APP_MONITOR_ID"
    );
    return null;
  }

  console.log(`🚀 Initializing CloudWatch RUM for sleephistory-frontend`);

  // Capture comprehensive user context
  globalUserContext = captureUserContext();
  console.log("📊 Captured user context:", {
    browser: globalUserContext.browser?.name,
    os: globalUserContext.os?.name,
    device: globalUserContext.device?.type,
    timezone: globalUserContext.locale?.timezone,
    screen: `${globalUserContext.screen?.width}x${globalUserContext.screen?.height}`,
  });

  try {
    // Dynamically import AwsRum to avoid SSR issues
    const { AwsRum } = require("aws-rum-web");

    const config = {
      sessionSampleRate: 1.0, // Sample 100% of sessions
      identityPoolId,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ["performance", "errors", "http"],
      allowCookies: true,
      enableXRay: true,
      guestRoleArn,
      // Event batching configuration
      recordResourceUrl: true,
      dispatchInterval: 5000, // 5 seconds
      batchLimit: 10,
    };

    awsRum = new AwsRum(appMonitorId, "1.0.0", region, config);

    console.log("📊 RUM instance created, enabling data collection...");

    // Add only essential session attributes — these are appended to EVERY event's
    // metadata by the SDK, so keep this list minimal to avoid payload rejection.
    if (awsRum) {
      const formParams = (() => {
        try {
          const params = new URLSearchParams(sessionStorage.getItem("searchParams") || "");
          return {
            formId:     params.get("formId")     || "unknown",
            practiceId: params.get("practiceId") || params.get("practice_id") || "unknown",
            tenantId:   params.get("tenantId")   || params.get("tenant_id")   || "unknown",
            saxId:      params.get("saxId")       || params.get("sax_id")      || "unknown",
            email:      params.get("email")       || "unknown",
          };
        } catch {
          return { formId: "unknown", practiceId: "unknown", tenantId: "unknown", saxId: "unknown", email: "unknown" };
        }
      })();

      awsRum.addSessionAttributes({
        // App
        appNameId: "sleephistory",
        // Form identifiers (used for filtering in CloudWatch)
        formId:     formParams.formId,
        practiceId: formParams.practiceId,
        tenantId:   formParams.tenantId,
        saxId:      formParams.saxId,
        email:      formParams.email,
        // Minimal device context
        deviceType: globalUserContext?.device?.type || "desktop",
        browser:    globalUserContext?.browser?.name || "Unknown",
        osName:     globalUserContext?.os?.name || "Unknown",
        timezone:   globalUserContext?.locale?.timezone || "Unknown",
      });

      // Fire full device/browser context once as a single custom event instead
      // of repeating it on every event via session attributes.
      recordFullUserContext();
    }

    // Asynchronously fetch IP location (no permission needed)
    fetchIpLocationAndUpdate();

    console.log("✅ CloudWatch RUM initialized successfully");
    return awsRum;
  } catch (error) {
    console.error("❌ Failed to initialize CloudWatch RUM:", error);
    return null;
  }
}

/**
 * Fetch IP location asynchronously and update RUM session attributes
 */
async function fetchIpLocationAndUpdate() {
  try {
    const ipLocation = await fetchIpLocation();
    if (ipLocation && awsRum) {
      console.log("📍 IP location captured:", {
        city: ipLocation.city,
        region: ipLocation.region,
        country: ipLocation.country,
      });

      awsRum.addSessionAttributes({
        ipAddress: ipLocation.ip || "Unknown",
        ipCity: ipLocation.city || "Unknown",
        ipRegion: ipLocation.region || "Unknown",
        ipRegionCode: ipLocation.regionCode || "Unknown",
        ipCountry: ipLocation.country || "Unknown",
        ipCountryCode: ipLocation.countryCode || "Unknown",
        ipContinent: ipLocation.continent || "Unknown",
        ipPostalCode: ipLocation.postalCode || "Unknown",
        ipLatitude: ipLocation.latitude || 0,
        ipLongitude: ipLocation.longitude || 0,
        ipTimezone: ipLocation.timezone || "Unknown",
        ipCurrency: ipLocation.currency || "Unknown",
        ipIsp: ipLocation.isp || "Unknown",
        ipAsn: ipLocation.asn || "Unknown",
      });

      // Update global context
      if (globalUserContext) {
        globalUserContext.ipLocation = ipLocation;
      }
    }
  } catch (error) {
    console.debug("Could not fetch IP location:", error);
  }
}

/**
 * Get the current RUM instance
 */
export function getRumInstance() {
  return awsRum;
}

/**
 * Get the captured user context
 */
export function getUserContext() {
  return globalUserContext;
}

/**
 * Update the current page in session context
 */
export function updatePageContext() {
  updateSessionContext();
  if (awsRum && typeof window !== "undefined") {
    awsRum.addSessionAttributes({
      currentPage: window.location.pathname,
    });
  }
}

/**
 * Record a custom event with automatic user context enrichment
 */
export function recordRumEvent(eventType: string, eventData: any) {
  if (!awsRum) {
    console.warn(`⚠️ RUM not initialized - event not recorded: ${eventType}`);
    return;
  }

  try {
    // Merge event data with minimal context
    const enrichedData = {
      ...eventData,
      _currentPage:
        typeof window !== "undefined" ? window.location.pathname : undefined,
      _onLine: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    };
    awsRum.recordEvent(eventType, enrichedData);
    console.debug(`✅ RUM event recorded: ${eventType}`, enrichedData);
  } catch (error) {
    console.error(`❌ Failed to record RUM event: ${eventType}`, error);
  }
}

/**
 * Record an error event
 */
export function recordRumError(error: Error, metadata?: Record<string, any>) {
  if (!awsRum) {
    console.warn("⚠️ RUM not initialized - error not recorded");
    return;
  }

  try {
    awsRum.recordError(error);
    if (metadata) {
      recordRumEvent("error_context", {
        message: error.message,
        stack: error.stack,
        ...metadata,
      });
    }
    console.debug("✅ RUM error recorded:", error.message);
  } catch (e) {
    console.error("❌ Failed to record RUM error:", e);
  }
}

/**
 * Add session attributes (user ID, practice ID, etc.)
 */
export function addRumSessionAttributes(
  attributes: Record<string, string | number | boolean>
) {
  if (!awsRum) {
    console.warn("⚠️ RUM not initialized - attributes not added");
    return;
  }

  try {
    awsRum.addSessionAttributes(attributes);
    console.debug("✅ RUM session attributes added:", attributes);
  } catch (error) {
    console.error("❌ Failed to add RUM session attributes:", error);
  }
}

/**
 * Record full user context as a custom event
 * Useful for capturing all browser context in one event
 */
export function recordFullUserContext() {
  if (!awsRum || !globalUserContext) {
    console.warn("⚠️ RUM not initialized - user context not recorded");
    return;
  }

  try {
    // Flatten the context for easier querying in CloudWatch
    const flatContext: Record<string, any> = {
      // User Agent
      userAgent: globalUserContext.userAgent,
      
      // Browser
      browserName: globalUserContext.browser?.name,
      browserVersion: globalUserContext.browser?.version,
      browserPlatform: globalUserContext.browser?.platform,
      browserVendor: globalUserContext.browser?.vendor,
      browserAppName: globalUserContext.browser?.appName,
      browserAppVersion: globalUserContext.browser?.appVersion,
      browserCookiesEnabled: globalUserContext.browser?.cookiesEnabled,
      browserDoNotTrack: globalUserContext.browser?.doNotTrack,
      
      // User Agent Client Hints
      uaDataMobile: globalUserContext.userAgentData?.mobile,
      uaDataPlatform: globalUserContext.userAgentData?.platform,
      uaDataBrands: JSON.stringify(globalUserContext.userAgentData?.brands),
      
      // Device
      deviceType: globalUserContext.device?.type,
      deviceVendor: globalUserContext.device?.vendor,
      deviceHardwareConcurrency: globalUserContext.device?.hardwareConcurrency,
      deviceMemory: globalUserContext.device?.deviceMemory,
      deviceMaxTouchPoints: globalUserContext.device?.maxTouchPoints,
      
      // OS
      osName: globalUserContext.os?.name,
      osVersion: globalUserContext.os?.version,
      
      // Screen
      screenWidth: globalUserContext.screen?.width,
      screenHeight: globalUserContext.screen?.height,
      screenAvailWidth: globalUserContext.screen?.availWidth,
      screenAvailHeight: globalUserContext.screen?.availHeight,
      screenColorDepth: globalUserContext.screen?.colorDepth,
      screenPixelRatio: globalUserContext.screen?.devicePixelRatio,
      screenOrientation: globalUserContext.screen?.orientation,
      
      // Window
      windowInnerWidth: globalUserContext.window?.innerWidth,
      windowInnerHeight: globalUserContext.window?.innerHeight,
      windowOuterWidth: globalUserContext.window?.outerWidth,
      windowOuterHeight: globalUserContext.window?.outerHeight,
      windowScreenX: globalUserContext.window?.screenX,
      windowScreenY: globalUserContext.window?.screenY,
      
      // Network
      networkEffectiveType: globalUserContext.network?.effectiveType,
      networkDownlink: globalUserContext.network?.downlink,
      networkRtt: globalUserContext.network?.rtt,
      networkSaveData: globalUserContext.network?.saveData,
      networkOnLine: globalUserContext.network?.onLine,
      
      // Locale
      localeLanguage: globalUserContext.locale?.language,
      localeLanguages: globalUserContext.locale?.languages?.join(","),
      localeTimezone: globalUserContext.locale?.timezone,
      localeTimezoneOffset: globalUserContext.locale?.timezoneOffset,
      localeEstimatedRegion: globalUserContext.locale?.estimatedRegion,
      
      // Document
      documentTitle: globalUserContext.document?.title,
      documentCharacterSet: globalUserContext.document?.characterSet,
      documentVisibilityState: globalUserContext.document?.visibilityState,
      documentReferrer: globalUserContext.document?.referrer,
      
      // Session
      sessionReferrer: globalUserContext.session?.referrer,
      sessionReferrerDomain: globalUserContext.session?.referrerDomain,
      sessionLandingPage: globalUserContext.session?.landingPage,
      sessionHostname: globalUserContext.session?.hostname,
      sessionProtocol: globalUserContext.session?.protocol,
      sessionOrigin: globalUserContext.session?.origin,
      sessionNavigationType: globalUserContext.session?.navigationType,
      sessionHistoryLength: globalUserContext.session?.historyLength,
      
      // Hardware
      hardwareGpuVendor: globalUserContext.hardware?.gpuVendor,
      hardwareGpuRenderer: globalUserContext.hardware?.gpuRenderer,
      
      // Memory
      memoryJsHeapSizeLimit: globalUserContext.memory?.jsHeapSizeLimit,
      memoryTotalJSHeapSize: globalUserContext.memory?.totalJSHeapSize,
      memoryUsedJSHeapSize: globalUserContext.memory?.usedJSHeapSize,
      
      // Features
      featureCookiesEnabled: globalUserContext.features?.cookiesEnabled,
      featureDoNotTrack: globalUserContext.features?.doNotTrack,
      featureOnLine: globalUserContext.features?.onLine,
      featureTouchEnabled: globalUserContext.features?.touchEnabled,
      featureWebdriver: globalUserContext.features?.webdriver,
      
      // Capabilities (subset of most useful)
      capWebGL: globalUserContext.capabilities?.webGL,
      capServiceWorker: globalUserContext.capabilities?.serviceWorker,
      capGeolocation: globalUserContext.capabilities?.geolocation,
      capNotifications: globalUserContext.capabilities?.notifications,
      capIndexedDB: globalUserContext.capabilities?.indexedDB,
      
      // IP Location (if captured)
      ipAddress: globalUserContext.ipLocation?.ip,
      ipCity: globalUserContext.ipLocation?.city,
      ipRegion: globalUserContext.ipLocation?.region,
      ipCountry: globalUserContext.ipLocation?.country,
      ipCountryCode: globalUserContext.ipLocation?.countryCode,
      ipLatitude: globalUserContext.ipLocation?.latitude,
      ipLongitude: globalUserContext.ipLocation?.longitude,
      ipIsp: globalUserContext.ipLocation?.isp,
      
      // Date/Time
      dateTimestamp: globalUserContext.dateTime?.timestamp,
      dateDayOfWeek: globalUserContext.dateTime?.dayOfWeek,
      dateIsWeekend: globalUserContext.dateTime?.isWeekend,
    };
    
    awsRum.recordEvent("shh.user_context_full", flatContext);
    console.debug("✅ Full user context recorded");
  } catch (error) {
    console.error("❌ Failed to record full user context:", error);
  }
}

interface RumProviderProps {
  children: React.ReactNode;
}

/**
 * RUM Provider Component
 * Wrap your app with this to enable CloudWatch RUM tracking
 */
export default function RumProvider({ children }: RumProviderProps) {
  // initializeRum() is called synchronously in index.tsx before root.render(),
  // so it is always initialized before any child component useEffect hooks run.
  // This useEffect is kept as a safety fallback only.
  useEffect(() => {
    initializeRum();
  }, []);

  return <>{children}</>;
}
